import React, { useState, useEffect } from 'react';
import CollapsibleSection from '../CollapsibleSection';
import IconDescriptionTile from '../IconDescriptionTile';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@mui/material';
import { IApplication } from '../../IApplication';

interface IAppLauncherAppsProps {
  applications: Record<string, IApplication>;
  searchKey: string;
  closeModal: () => void;
}

const useStyles = makeStyles()((theme) => ({
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
  },
  headerTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    marginTop: '2rem',
  },
  headerSubtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  tilesContainer: {
    display: 'flex',
    flexWrap: 'nowrap',
    gap: '20px', // Adjusted for better spacing
    justifyContent: 'flex-start', // Center-align tiles
  },
  tile: {
    width: '100%',
    minWidth: '200px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
    '@media only screen and (min-width: 750px)': {
      width: 'calc(50% - 20px)', // Two columns on medium screens
    },
    '@media only screen and (min-width: 1024px)': {
      width: 'calc(33.33% - 20px)', // Three columns on larger screens
    },
    '@media only screen and (min-width: 1440px)': {
      width: 'calc(25% - 20px)', // Four columns on extra-large screens
    },
  },
  emptyState: {
    textAlign: 'center',
    marginTop: theme.spacing(4),
    fontSize: '1rem',
    color: theme.palette.text.secondary,
  },
}));

const AppLauncherApps: React.FC<IAppLauncherAppsProps> = ({
  applications,
  closeModal,
  searchKey,
}) => {
  const { classes } = useStyles();
  const [filteredApplications, setFilteredApplications] = useState(applications);

  useEffect(() => {
    if (searchKey && searchKey.trim()) {
      const newApps: Record<string, IApplication> = {};
      Object.keys(applications).forEach((appKey) => {
        if (
          applications[appKey].title
            .toLowerCase()
            .includes(searchKey.toLowerCase())
        ) {
          newApps[appKey] = applications[appKey];
        }
      });
      setFilteredApplications(newApps);
    } else {
      setFilteredApplications(applications);
    }
  }, [applications, searchKey]);

  const getAppPath = (app: IApplication) => app.route;

  return (
    <>
      <div className={classes.header}>
        <Typography className={classes.headerTitle}>All Applications</Typography>
        <Typography className={classes.headerSubtitle}>
          Browse through your available apps below
        </Typography>
      </div>

      <CollapsibleSection
        title="Applications"
        className={classes.tilesContainer}
        open
      >
        {Object.keys(filteredApplications).length > 0 ? (
          Object.keys(filteredApplications).map((appName) => (
            <IconDescriptionTile
              key={appName}
              title={filteredApplications[appName].title}
              imgSrc={filteredApplications[appName].icon}
              description={filteredApplications[appName].description}
              route={getAppPath(filteredApplications[appName])}
              className={classes.tile}
              onClick={closeModal}
            />
          ))
        ) : (
          <Typography className={classes.emptyState}>
            No applications found. Try refining your search.
          </Typography>
        )}
      </CollapsibleSection>
    </>
  );
};

export default AppLauncherApps;
